/* eslint-disable camelcase */
import { useBroadcastChannel } from "@app-context/broadcastChannels/context";
import { APISources } from "@app-context/networkCalls/types";
import {
  useOEMS,
  useOEMSBlotterRowsTransaction,
} from "@app-context/oems/context";
import { createOrderColumnDefs } from "@app-utils/oems/createColumnDefs";
import { useRefCallback } from "@enfusion-ui/hooks";
import { WebReportTableSyncRow } from "@enfusion-ui/types";
import {
  CenterContent,
  ContentMessage,
  SecondaryTabList,
  Spinner,
  useConfirmationModal,
  VisibleIf,
} from "@enfusion-ui/web-components";
import {
  errorToast,
  orderCellFormatter,
  orderCellStyle,
  styled,
  useAuth,
  useThisTab,
} from "@enfusion-ui/web-core";
import { faDesktop } from "@fortawesome/pro-solid-svg-icons";
import { CellClickedEvent, ColDef, GridReadyEvent } from "ag-grid-community";
import * as React from "react";
import ReactPrismEditor from "react-prism-editor";
import { v4 as uuidv4 } from "uuid";
import OMSContentView from "view/OEMSContentView";

import { TabularData } from "../../components/TabularData";
import {
  InspectorOEMSConfig,
  useInspectorContext,
} from "../../context/inspector/context";
import {
  ContentProp,
  Line,
  ResponseContainer,
  ResponseTab,
} from "./InspectorContent";
import { PositionData, StreamData } from "./ReportContent";

const OEMSMainContentInnerContainer = styled.div`
  flex: 1;
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: var(--spacing);
  border-radius: var(--radius-l);
  box-sizing: border-box;
`;

const getResponseTabs = () => {
  const tabs = [
    {
      label: "Raw data",
      key: "raw-data",
    },
    {
      label: "Tabular Data",
      key: "tabular-data",
    },
  ];
  return tabs;
};

const streamRawColumnDefs = [
  {
    colId: "direction",
    field: "direction",
    cellRenderer: "directionCellRenderer",
    width: 30,
  },
  {
    colId: "data",
    field: "data",
    headerName: "Data",
    width: 1200,
  },
  {
    colId: "length",
    field: "length",
    headerName: "Length",
    width: 80,
  },
  {
    colId: "timestamp",
    field: "timestamp",
    headerName: "Timestamp",
    width: 120,
  },
];

export const OEMSContent: React.FC<ContentProp> = ({ setParamsOpen }) => {
  const [error, setError] = React.useState<Error | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [gridData, setGridData] = React.useState<PositionData>({
    columnDefs: [],
    rowData: [],
  });
  const [streamGridData, setStreamGridData] = React.useState<StreamData>({
    columnDefs: [],
    rowData: null,
  });
  const [cellClickedData, setCellClickedData] = React.useState("");
  const [selectedContentTab, setSelectedContentTab] =
    React.useState<string>("tabular-data");
  const [colDefs, setColDefs] = React.useState<ColDef[]>([]);

  const gridRef = React.useRef<GridReadyEvent>();
  const signalRef = React.useRef(new AbortController());
  const closeTabRef = React.useRef(false);
  const currentUser = React.useRef<string>();
  const callBackQueueRef = React.useRef<any[]>([]);
  const {
    orderColumns,
    metadata,
    messages,
    initializeOEMS,
    isInitialized,
    closeSocket,
  } = useOEMS();

  const {
    config: configBase,
    getSecureToken,
    apiSource,
  } = useInspectorContext();
  const transactions = useOEMSBlotterRowsTransaction();
  const { id, onTabWillClose, closeTab } = useThisTab();
  const { user } = useAuth();
  const config = configBase as InspectorOEMSConfig;
  const { username } = config;

  React.useEffect(() => {
    if (isInitialized && callBackQueueRef.current?.length) {
      const callback = callBackQueueRef.current.shift();
      callback?.();
    }
    return onTabWillClose(() => {
      if (isInitialized) {
        openCloseTabConfirmationModal();
        return closeTabRef.current;
      }
      return true;
    });
  }, [isInitialized, closeTabRef.current, callBackQueueRef.current]);

  const openCloseTabConfirmationModal = () => {
    closeTabConfirmation.openModal();
  };

  const handleCloseConnection = () => {
    closeTabRef.current = true;
    closeSocket();
    const applicationSessionStorageKey = [
      "applicationSessionToken",
      currentUser.current,
    ]
      .filter(Boolean)
      .join("///");

    sessionStorage.removeItem(applicationSessionStorageKey);
    closeTabConfirmation.closeModal();
    closeTab();
  };

  const closeTabConfirmation = useConfirmationModal({
    title: "Close OEMS session",
    renderContent: () =>
      "Closing this tab will end current oems session. Do you want to continue?",
    onSubmit: handleCloseConnection,
    submitActionTheme: "danger",
  });

  React.useEffect(() => {
    if (orderColumns && metadata) {
      setColDefs(createOrderColumnDefs(orderColumns, metadata));
    }
  }, [metadata, orderColumns]);

  React.useEffect(() => {
    if (messages?.length) {
      const rowData = messages.map(
        (i, idx) =>
          ({
            ...i,
            __row_id: uuidv4(),
            __row_idx: idx,
          } as any)
      );

      setStreamGridData(() => ({
        columnDefs: streamRawColumnDefs,
        rowData,
      }));
    }
  }, [messages]);

  React.useEffect(() => {
    const { action } = transactions;
    if (gridRef.current && action) {
      switch (action) {
        case "blotter-sync": {
          const addRows = transactions?.add;
          const callback = transactions?.callback;
          if (addRows)
            if (addRows) {
              const rowData = addRows.map(
                (i, idx) =>
                  ({
                    ...i.columnValues,
                    __row_id: i.orderId,
                    __row_idx: idx,
                    orderSide: i.orderSide,
                  } as unknown as WebReportTableSyncRow)
              );
              setGridData({
                columnDefs: colDefs,
                rowData: rowData,
              });
              callback?.(addRows);
            }
          break;
        }
        case "blotter-row-update":
        case "blotter-rows-update": {
          const addRowData = transactions?.add ?? [];
          const updateRows = transactions?.update ?? [];
          const addRows = addRowData.map(
            (i, idx) =>
              ({
                ...i.columnValues,
                __row_id: i.orderId,
                __row_idx: idx,
                orderSide: i.orderSide,
              } as any)
          );
          const updateRowsBase = updateRows.map(
            (i, idx) =>
              ({
                ...i.columnValues,
                __row_id: i.orderId,
                __row_idx: idx,
                orderSide: i.orderSide,
              } as any)
          );

          const callback = transactions?.callback;

          if (addRows && addRows.length > 0) {
            callback?.(addRowData);
          }
          gridRef.current.api.applyTransaction({ add: addRows });
          gridRef.current.api.applyTransactionAsync({
            update: updateRowsBase,
          });
          callback?.(updateRows);
          break;
        }
        case "order-delete": {
          const callback = transactions?.callback;

          const removeRows = transactions?.remove ?? [];
          gridRef.current.api.applyTransactionAsync(
            {
              remove: removeRows,
            },
            () => {
              if (removeRows.length > 0) {
                callback?.(removeRows);
              }
            }
          );
          break;
        }
      }
    }
  }, [transactions, gridRef.current]);

  const onGridReady = (gridReadyEvent: GridReadyEvent) => {
    gridRef.current = gridReadyEvent;
    const displayedRow = gridReadyEvent.api.getDisplayedRowAtIndex(0);
    if (displayedRow) displayedRow.setSelected(true, true);
  };

  const handleSend = useRefCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      let appSessionToken = await getSecureToken();
      if (apiSource === APISources.CORE && !appSessionToken) {
        return;
      }
      if (apiSource === APISources.INTERNAL && !user) {
        errorToast(
          "Failed to load OEMS.",
          "Please login to continue internally."
        );
        return;
      }
      if (!isInitialized) {
        currentUser.current = username;
        initializeOEMS(appSessionToken);
      }

      signalRef.current = new AbortController();
      setParamsOpen(false);
    } catch (err: any) {
      errorToast("Error occurred ", (err as Error).message);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [isInitialized, username, apiSource, user]);

  useBroadcastChannel("inspector-channel", (msg) => {
    const [msgId, type] = msg.split("<=>");
    if (msgId === id) {
      switch (type) {
        case "send":
          handleSend();
          break;
        case "cancel":
          signalRef.current.abort();
          setError(null);
          break;
        default:
          break;
      }
    }
  });

  const onCellClicked = useRefCallback((event: CellClickedEvent) => {
    setCellClickedData(event.data.data);
  }, []);

  return (
    <>
      <Line />
      <ResponseTab>
        {loading ? (
          <CenterContent>
            <Spinner />
          </CenterContent>
        ) : error ? (
          <CenterContent>
            <ContentMessage message={error.message} />
          </CenterContent>
        ) : streamGridData.rowData ? (
          <>
            <SecondaryTabList
              value={selectedContentTab}
              onSelect={setSelectedContentTab}
              tabs={getResponseTabs()}
            />
            <ResponseContainer>
              <VisibleIf visible={selectedContentTab === "raw-data"}>
                <div
                  style={{
                    width: "100%",
                    flex: 1,
                    height: cellClickedData
                      ? "calc(50% - 1px - var(--spacing))"
                      : "100%",
                  }}
                >
                  <TabularData
                    colDefs={streamGridData.columnDefs}
                    rowData={streamGridData.rowData}
                    onCellClicked={onCellClicked}
                  />
                </div>
                {cellClickedData && (
                  <>
                    <Line style={{ marginTop: "var(--spacing)" }} />
                    <ReactPrismEditor
                      language="json"
                      theme="tomorrow"
                      code={JSON.stringify(
                        JSON.parse(cellClickedData),
                        null,
                        2
                      )}
                      lineNumber
                      readOnly
                      clipboard
                    />
                  </>
                )}
              </VisibleIf>
              <VisibleIf visible={selectedContentTab === "tabular-data"}>
                <OEMSMainContentInnerContainer>
                  <OMSContentView>
                    <TabularData
                      metadata={metadata}
                      height="100%"
                      colDefs={gridData.columnDefs}
                      onGridReady={onGridReady}
                      rowData={gridData.rowData || []}
                      defaultColDef={{
                        valueFormatter: orderCellFormatter,
                        cellStyle: orderCellStyle,
                      }}
                    />
                  </OMSContentView>
                </OEMSMainContentInnerContainer>
              </VisibleIf>
            </ResponseContainer>
          </>
        ) : (
          <CenterContent>
            <ContentMessage icon={faDesktop} message="Load Content" />
          </CenterContent>
        )}
        {closeTabConfirmation.content}
      </ResponseTab>
    </>
  );
};
